import { MatchState, CategoryAssignment } from '@/types';
import { getMatchWithDetails, getPlayerOfMatch } from '@/lib/supabase';

// ─── Match Report Export ───
export async function exportMatchReport(matchState: MatchState, _teams: any[]) {
  const XLSX = await import('xlsx');
  const match = matchState.match!;
  const teamA = matchState.teamA!;
  const teamB = matchState.teamB!;

  const matchWinner = teamA.setsWon > teamB.setsWon ? teamA.name : teamB.name;
  const tossWonBy = teamA.id === match.tossWonById ? teamA.name : teamB.name;

  // Fetch Player of the Match
  let playerOfMatchNames: string[] = [];
  if (match.id) {
    try {
      const winners = await getPlayerOfMatch(match.id);
      playerOfMatchNames = winners.map(w => `${w.playerName} (${w.teamName})`);
    } catch (err) {
      console.error('[ExportMatchReport] getPlayerOfMatch failed:', err);
    }
  }

  // Sheet 1: Match Summary
  const summaryData = [
    ['Field', 'Value'],
    ['Match Number', match.matchNumber],
    ['Match Date', match.date],
    ['Team A', teamA.name],
    ['Team B', teamB.name],
    ['Match Type', match.matchType],
    ['Toss Won By', tossWonBy],
    ['Choose To', match.tossChoice],
    ['Match Winner', matchWinner],
    ['Player of the Match', playerOfMatchNames.length > 0 ? playerOfMatchNames.join(', ') : 'Not determined'],
  ];

  // Set scores
  const setScores = matchState.sets.map(set => {
    const winner = teamA.id === set.winnerId ? teamA.name : teamB.name;
    return {
      'Set Number': set.setNumber,
      'Team A Score': set.scoreA,
      'Team B Score': set.scoreB,
      'Set Winner': winner,
      'Start Time': set.startedAt,
      'End Time': set.endedAt,
    };
  });

  const fullMatchData = await getMatchWithDetails(match.id);
  const allPlayers = [...teamA.players, ...teamB.players];

  // Rally history with categories — ordered by Set Number then Rally Number
  const rallyHistory = [...fullMatchData.sets]
    .sort((a: any, b: any) => a.set_number - b.set_number)
    .flatMap((set: any) => {
    return [...set.rallies]
      .sort((a: any, b: any) => a.rally_number - b.rally_number)
      .map((rally: any) => {
      const server = allPlayers.find(p => p.id === rally.server_id);
      const serverName = rally.is_server_fault ? 'SERVER_FAULT' : (server ? server.name : '');
      const winningTeam = rally.point_won_by_id === teamA.id ? teamA : teamB;

      // Format categories for display
      let categoriesStr = '';
      if (rally.categories && Array.isArray(rally.categories)) {
        const catLabels = rally.categories.map((cat: CategoryAssignment) => {
          const player = allPlayers.find(p => p.id === cat.playerId);
          return `${cat.category}:${player?.name ?? '?'}`;
        });
        categoriesStr = catLabels.join(', ');
      }

      return {
        'Set Number': set.set_number,
        'Rally Number': rally.rally_number,
        'Serving Team': rally.serving_team_id === teamA.id ? teamA.name : teamB.name,
        'Server': serverName,
        'Winning Team': winningTeam ? winningTeam.name : '',
        'Categories': categoriesStr,
      };
    });
  });

  // Team stats per set
  const teamStats = fullMatchData.sets.map((set: any) => {
    const teamARallies = set.rallies.filter((r: any) => r.point_won_by_id === teamA.id);
    return {
      'Set Number': set.set_number,
      'Team A Score': set.score_a,
      'Team B Score': set.score_b,
      'Team A Rallies Won': teamARallies.length,
      'Team B Rallies Won': set.rallies.length - teamARallies.length,
    };
  });

  // Player Statistics - compute volleyball categories for each player
  const playerStatsMap = new Map<string, {
    playerName: string;
    teamShortName: string;
    acer: number;
    defender: number;
    libero: number;
    setter: number;
    blocker: number;
    finisher: number;
    total: number;
  }>();

  const ensurePlayer = (playerId: string) => {
    if (!playerStatsMap.has(playerId)) {
      const player = allPlayers.find(p => p.id === playerId);
      const team = teamA.players.find(p => p.id === playerId) ? teamA : teamB;
      playerStatsMap.set(playerId, {
        playerName: player?.name ?? playerId,
        teamShortName: team?.shortName ?? '',
        acer: 0, defender: 0, libero: 0, setter: 0, blocker: 0, finisher: 0, total: 0
      });
    }
    return playerStatsMap.get(playerId)!;
  };

  for (const set of fullMatchData.sets) {
    for (const rally of set.rallies) {
      if (!rally.categories || !Array.isArray(rally.categories)) continue;
      for (const cat of rally.categories) {
        if (!cat.playerId || !cat.category) continue;
        const entry = ensurePlayer(cat.playerId);
        switch (cat.category.toUpperCase()) {
          case 'A': entry.acer += 1; break;
          case 'D': entry.defender += 1; break;
          case 'L': entry.libero += 1; break;
          case 'S': entry.setter += 1; break;
          case 'B': entry.blocker += 1; break;
          case 'F': entry.finisher += 1; break;
        }
      }
    }
  }

  // Sort by total descending
  const playerStatsArray = Array.from(playerStatsMap.values())
    .map(r => ({ ...r, total: r.acer + r.defender + r.libero + r.setter + r.blocker + r.finisher }))
    .sort((a, b) => b.total - a.total);

  const playerStatsRows = [
    ['Rank', 'Player', 'Team', 'A', 'D', 'L', 'S', 'B', 'F', 'Total'],
    ...playerStatsArray.map((row, idx) => [
      idx + 1,
      row.playerName,
      row.teamShortName,
      row.acer,
      row.defender,
      row.libero,
      row.setter,
      row.blocker,
      row.finisher,
      row.acer + row.defender + row.libero + row.setter + row.blocker + row.finisher,
    ]),
  ];

  const wb = XLSX.utils.book_new();

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Match Summary');

  const setScoresSheet = XLSX.utils.json_to_sheet(setScores);
  XLSX.utils.book_append_sheet(wb, setScoresSheet, 'Set Scores');

  const rallyHistorySheet = XLSX.utils.json_to_sheet(rallyHistory);
  XLSX.utils.book_append_sheet(wb, rallyHistorySheet, 'Rally History');

  const teamStatsSheet = XLSX.utils.json_to_sheet(teamStats);
  XLSX.utils.book_append_sheet(wb, teamStatsSheet, 'Team Stats');

  const playerStatsSheet = XLSX.utils.aoa_to_sheet(playerStatsRows);
  XLSX.utils.book_append_sheet(wb, playerStatsSheet, 'Player Statistics');

  XLSX.writeFile(wb, `match_${match.matchNumber}_report.xlsx`);
}

export async function exportSetData(matchState: MatchState, setNumber: number) {
  const XLSX = await import('xlsx');
  const match = matchState.match!;
  const teamA = matchState.teamA!;
  const teamB = matchState.teamB!;
  const set = matchState.sets[setNumber - 1];

  if (!set) {
    console.error('[exportSetData] Set not found:', setNumber);
    return;
  }

  const allPlayers = [...teamA.players, ...teamB.players];

  const wb = XLSX.utils.book_new();

  const summaryData = [
    ['Field', 'Value'],
    ['Match Number', match.matchNumber],
    ['Set Number', setNumber],
    ['Team A', teamA.name],
    ['Team B', teamB.name],
    ['Set Winner', teamA.id === set.winnerId ? teamA.name : teamB.name],
  ];

  const rallyData = (set.rallies ?? []).map(r => {
    const server = allPlayers.find(p => p.id === r.serverId);
    const serverName = r.isServerFault ? 'SERVER_FAULT' : (server?.name ?? '');
    const winningTeam = r.pointWonById === teamA.id ? teamA : teamB;

    // Format categories
    let categoriesStr = '';
    if (r.categories && Array.isArray(r.categories)) {
      const catLabels = r.categories.map((cat: CategoryAssignment) => {
        const player = allPlayers.find(p => p.id === cat.playerId);
        return `${cat.category}:${player?.name ?? '?'}`;
      });
      categoriesStr = catLabels.join(', ');
    }

    return {
      'Rally': r.rallyNumber,
      'Serving Team': r.servingTeamId === teamA.id ? teamA.name : teamB.name,
      'Server': serverName,
      'Winning Team': winningTeam.name,
      'Categories': categoriesStr,
    };
  });

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Set Summary');

  const rallySheet = XLSX.utils.json_to_sheet(rallyData);
  XLSX.utils.book_append_sheet(wb, rallySheet, 'Rallies');

  XLSX.writeFile(wb, `match_${match.matchNumber}_set_${setNumber}.xlsx`);
}

// ─── Tournament Export ───

export async function exportTournamentData(rawData: any, teams: any[], tournament?: { id?: string; name?: string; startDate?: string; endDate?: string; venue?: string | null } | string, participatingTeams?: any[]) {
  const XLSX = await import('xlsx');
  // Backwards-compatible: accept either a tournament object or a bare id string
  const tournamentObj = typeof tournament === 'string'
    ? { id: tournament }
    : (tournament ?? {});
  const tournamentId = tournamentObj.id;
  const allMatches = Array.isArray(rawData) ? rawData : rawData?.matches ?? [];

  // Filter to only matches from this tournament and only completed matches (for match summary/points table)
  const completedMatches = allMatches.filter((m: any) => {
    if (tournamentId && m.tournament_id !== tournamentId) return false;
    return m.status === 'match-won';
  });

  // For player statistics: include all non-discarded tournament matches (live + completed)
  const matches = allMatches.filter((m: any) => {
    if (tournamentId && m.tournament_id !== tournamentId) return false;
    return m.status !== 'discarded';
  });

  // Collect unique team IDs from all non-discarded tournament matches
  const teamIdsInTournament = new Set<string>();
  for (const m of matches) {
    teamIdsInTournament.add(m.team_a_id);
    teamIdsInTournament.add(m.team_b_id);
  }

  // Build tournamentTeams map - only include teams that have matches in this tournament
  const allTeamPlayers: any[] = [];
  const tournamentTeams: any[] = (teams ?? [])
    .filter((t: any) => teamIdsInTournament.has(t.id))
    .map((t: any) => {
      const players = Array.isArray(t.players) ? t.players : [];
      players.forEach((p: any) => allTeamPlayers.push({ ...p, team_id: t.id }));
      return {
        id: t.id,
        name: t.name,
        short_name: t.shortName,
        color: t.color,
        players,
      };
    });

  console.log('[ExportTournamentData] Teams in tournament:', tournamentTeams.length);
  console.log('[ExportTournamentData] allTeamPlayers count:', allTeamPlayers.length);

  // Debug: Log first match structure
  if (matches.length > 0) {
    const firstMatch = matches[0];
    console.log('[ExportTournamentData] First match keys:', Object.keys(firstMatch));
    if (firstMatch.sets && firstMatch.sets.length > 0) {
      console.log('[ExportTournamentData] First set keys:', Object.keys(firstMatch.sets[0]));
      if (firstMatch.sets[0].rallies && firstMatch.sets[0].rallies.length > 0) {
        console.log('[ExportTournamentData] First rally keys:', Object.keys(firstMatch.sets[0].rallies[0]));
        console.log('[ExportTournamentData] First rally categories:', firstMatch.sets[0].rallies[0].categories);
      }
    }
  }

  // Check if rallies have categories
  let ralliesWithCategories = 0;
  let totalRallies = 0;
  let categoryExamples: any[] = [];
  for (const m of matches) {
    if (!m?.sets) continue;
    for (const s of m.sets ?? []) {
      for (const r of s?.rallies ?? []) {
        totalRallies++;
        if (r.categories && Array.isArray(r.categories) && r.categories.length > 0) {
          ralliesWithCategories++;
          if (categoryExamples.length < 3) {
            categoryExamples.push(r.categories);
          }
        }
      }
    }
  }
  console.log('[ExportTournamentData] Total rallies:', totalRallies, 'With categories:', ralliesWithCategories);
  console.log('[ExportTournamentData] Category examples:', categoryExamples);

  // Helpers
  const safeArr = (v: any) => (Array.isArray(v) ? v : []);
  const safeStr = (v: any) => (v == null ? '' : String(v));
  const safeNum = (v: any) => (typeof v === 'number' ? v : 0);
  const safeTeamName = (id: string | null | undefined) => {
    const t = tournamentTeams.find((x: any) => x.id === id);
    return t?.name ?? '';
  };
  const safeTeamFromMatch = (match: any, id: string | null | undefined) => {
    const fromTeams = tournamentTeams.find((t: any) => t.id === id);
    if (fromTeams) return fromTeams;
    const teamData = id === match.team_a_id ? match.team_a : match.team_b;
    if (teamData && typeof teamData === 'object') return { ...teamData, short_name: teamData.shortName ?? teamData.short_name };
    return { name: '', short_name: '' };
  };

  // ── Tournament Summary sheet ───────────────────────────────────
  const tournamentSummaryData = [
    ['Field', 'Value'],
    ['Tournament Name', safeStr(tournamentObj.name) || (tournamentId ? `Tournament ${tournamentId.slice(0, 8)}` : 'Tournament')],
    ['Start Date', safeStr(tournamentObj.startDate)],
    ['End Date', safeStr(tournamentObj.endDate)],
    ['Total Matches', matches.length],
    ['Completed Matches', completedMatches.length],
    ['Teams', tournamentTeams.length],
    ['Total Players', allTeamPlayers.length],
  ];

  // ── Tournament Details section (additional context block) ─────────
  const tournamentDetailsData = [
    ['Tournament Details'],
    ['Field', 'Value'],
    ['Name', safeStr(tournamentObj.name) || (tournamentId ? `Tournament ${tournamentId.slice(0, 8)}` : 'Tournament')],
    ['Start Date', safeStr(tournamentObj.startDate)],
    ['End Date', safeStr(tournamentObj.endDate)],
    ['Venue', safeStr(tournamentObj.venue)],
    ['Tournament ID', safeStr(tournamentId)],
    ['Total Players', allTeamPlayers.length],
    ['Generated At', new Date().toLocaleString()],
  ];

  // ── All Matches sheet ───────────────────────────────────────────
  const matchesData: (string | number)[][] = [
    ['Match #', 'Date', 'Type', 'Team A', 'Team B', 'Toss Won By', 'Choose To', 'Winner', 'Status', 'Player of the Match'],
  ];

  for (const m of completedMatches) {
    if (!m || typeof m !== 'object') continue;
    const teamA = safeTeamFromMatch(m, m.team_a_id);
    const teamB = safeTeamFromMatch(m, m.team_b_id);
    const tossWon = safeTeamName(m.toss_won_by_id);
    const winner = safeTeamName(m.winner_id);

    let playerOfMatch: string[] = [];
    if (m.player_of_match_ids && Array.isArray(m.player_of_match_ids) && m.player_of_match_ids.length > 0) {
      const pomTeamId = m.player_of_match_team_id ?? m.winner_id;
      const pomTeam = tournamentTeams.find((t: any) => t.id === pomTeamId);
      playerOfMatch = m.player_of_match_ids
        .map((pid: string) => {
          const p = pomTeam?.players?.find((x: any) => x.id === pid);
          return p ? `${p.name} (${pomTeam?.short_name ?? ''})` : '';
        })
        .filter(Boolean);
    }

    matchesData.push([
      safeNum(m.match_number),
      safeStr(m.date),
      safeStr(m.match_type),
      safeStr(teamA?.name),
      safeStr(teamB?.name),
      safeStr(tossWon),
      safeStr(m.toss_choice),
      safeStr(winner),
      safeStr(m.status),
      playerOfMatch.length > 0 ? playerOfMatch.join(', ') : 'Not determined',
    ]);
  }

  // ── All Set Scores sheet ─────────────────────────────────────────
  const setScoresData: (string | number)[][] = [
    ['Match #', 'Set #', 'Team A', 'Score A', 'Team B', 'Score B', 'Set Winner'],
  ];

  for (const m of completedMatches) {
    if (!m || !m.sets) continue;
    const teamA = safeTeamFromMatch(m, m.team_a_id);
    const teamB = safeTeamFromMatch(m, m.team_b_id);
    for (const s of safeArr(m.sets)) {
      if (!s) continue;
      const winner = (s.score_a ?? 0) > (s.score_b ?? 0) ? teamA : teamB;
      setScoresData.push([
        safeNum(m.match_number),
        safeNum(s.set_number),
        safeStr(teamA?.name),
        safeNum(s.score_a),
        safeStr(teamB?.name),
        safeNum(s.score_b),
        safeStr(winner?.name),
      ]);
    }
  }

  // ── Player Of The Match sheet ──────────────────────────────────
  const pomRows: (string | number)[][] = [['Match #', 'Player', 'Team', 'A', 'D', 'L', 'S', 'B', 'F', 'Total']];
  for (const m of completedMatches) {
    if (m?.status !== 'match-won' || !m.winner_id) continue;
    const winnerTeam = tournamentTeams.find((t: any) => t.id === m.winner_id);
    if (!winnerTeam) continue;

    // Compute category stats per player for this match
    const matchStars = new Map<string, { acer: number; defender: number; libero: number; setter: number; blocker: number; finisher: number }>();
    for (const s of safeArr(m.sets)) {
      for (const r of safeArr(s?.rallies)) {
        if (!r?.categories || !Array.isArray(r.categories)) continue;
        for (const cat of r.categories) {
          const playerId = cat.playerId || cat.player_id;
          const category = cat.category || cat.Category;
          if (!playerId || !category) continue;
          const entry = matchStars.get(playerId) ?? { acer: 0, defender: 0, libero: 0, setter: 0, blocker: 0, finisher: 0 };
          switch (category.toUpperCase()) {
            case 'A': entry.acer += 1; break;
            case 'D': entry.defender += 1; break;
            case 'L': entry.libero += 1; break;
            case 'S': entry.setter += 1; break;
            case 'B': entry.blocker += 1; break;
            case 'F': entry.finisher += 1; break;
          }
          matchStars.set(playerId, entry);
        }
      }
    }

    // Get Player of Match
    let pomPlayers: { id: string; name: string; team: string; acer: number; defender: number; libero: number; setter: number; blocker: number; finisher: number; total: number }[] = [];

    if (Array.isArray(m.player_of_match_ids) && m.player_of_match_ids.length > 0) {
      const pomTeamId = m.player_of_match_team_id ?? m.winner_id;
      const pomTeam = tournamentTeams.find((t: any) => t.id === pomTeamId);
      for (const pid of m.player_of_match_ids) {
        const player = pomTeam?.players?.find((p: any) => p.id === pid);
        const s = matchStars.get(pid) ?? { acer: 0, defender: 0, libero: 0, setter: 0, blocker: 0, finisher: 0 };
        if (player) {
          pomPlayers.push({ id: pid, name: player.name, team: pomTeam?.short_name ?? '', ...s, total: s.acer + s.defender + s.libero + s.setter + s.blocker + s.finisher });
        }
      }
    } else {
      let max = 0;
      for (const p of winnerTeam.players ?? []) {
        const s = matchStars.get(p.id);
        if (s) max = Math.max(max, s.acer + s.defender + s.libero + s.setter + s.blocker + s.finisher);
      }
      if (max > 0) {
        for (const p of winnerTeam.players ?? []) {
          const s = matchStars.get(p.id);
          if (s && (s.acer + s.defender + s.libero + s.setter + s.blocker + s.finisher) === max) {
            pomPlayers.push({ id: p.id, name: p.name, team: winnerTeam.short_name ?? '', ...s, total: max });
          }
        }
      }
    }

    for (const p of pomPlayers) {
      pomRows.push([safeNum(m.match_number), p.name, p.team, p.acer, p.defender, p.libero, p.setter, p.blocker, p.finisher, p.total]);
    }
  }

  // ── Points Table ───────────────────────────────────────────────
  const teamStats = new Map<string, { played: number; wins: number; losses: number; points: number; setsWon: number; setsLost: number; pointsScored: number; pointsConceded: number }>();
  for (const m of completedMatches) {
    const a = teamStats.get(m.team_a_id) ?? { played: 0, wins: 0, losses: 0, points: 0, setsWon: 0, setsLost: 0, pointsScored: 0, pointsConceded: 0 };
    const b = teamStats.get(m.team_b_id) ?? { played: 0, wins: 0, losses: 0, points: 0, setsWon: 0, setsLost: 0, pointsScored: 0, pointsConceded: 0 };
    a.played++; b.played++;
    const aSetsWon = safeArr(m.sets).filter((s: any) => s?.winner_id === m.team_a_id).length;
    const bSetsWon = safeArr(m.sets).filter((s: any) => s?.winner_id === m.team_b_id).length;
    a.setsWon += aSetsWon; b.setsWon += bSetsWon;
    a.setsLost += bSetsWon; b.setsLost += aSetsWon;
    const aPoints = safeArr(m.sets).reduce((sum: number, s: any) => sum + (s?.score_a ?? 0), 0);
    const bPoints = safeArr(m.sets).reduce((sum: number, s: any) => sum + (s?.score_b ?? 0), 0);
    a.pointsScored += aPoints; a.pointsConceded += bPoints;
    b.pointsScored += bPoints; b.pointsConceded += aPoints;
    if (m.winner_id === m.team_a_id) { a.wins++; a.points += 2; b.losses++; }
    else { b.wins++; b.points += 2; a.losses++; }
    teamStats.set(m.team_a_id, a); teamStats.set(m.team_b_id, b);
  }
  const sortedTeams = [...teamStats.entries()].sort((a, b) => b[1].points - a[1].points || (b[1].setsWon - b[1].setsLost) - (a[1].setsWon - a[1].setsLost));

  const pointsTableRows: (string | number)[][] = [
    ['Position', 'Team', 'Played', 'Won', 'Lost', 'Points', 'Sets Won', 'Sets Lost', 'Set Win %', 'Points Scored', 'Points Conceded', 'Net Points'],
    ...sortedTeams.map(([teamId, stats], idx) => [
      idx + 1,
      safeTeamName(teamId),
      stats.played,
      stats.wins,
      stats.losses,
      stats.points,
      stats.setsWon,
      stats.setsLost,
      stats.played > 0 ? ((stats.setsWon / (stats.setsWon + stats.setsLost)) * 100).toFixed(1) : '0.0',
      stats.pointsScored,
      stats.pointsConceded,
      stats.pointsScored - stats.pointsConceded,
    ]),
  ];

  // ── Player Statistics (aggregated across all non-discarded tournament matches) ─────────────
  const playerStatsMap = new Map<string, {
    playerName: string;
    teamShortName: string;
    acer: number;
    defender: number;
    libero: number;
    setter: number;
    blocker: number;
    finisher: number;
  }>();

  const ensurePlayer = (playerId: string) => {
    if (!playerStatsMap.has(playerId)) {
      const p = allTeamPlayers.find((x: any) => x.id === playerId);
      const team = tournamentTeams.find((t: any) => t.id === (p?.team_id ?? p?.teamId));
      playerStatsMap.set(playerId, {
        playerName: p?.name ?? playerId,
        teamShortName: team?.short_name ?? '',
        acer: 0, defender: 0, libero: 0, setter: 0, blocker: 0, finisher: 0,
      });
    }
    return playerStatsMap.get(playerId)!;
  };

  for (const m of matches) {
    if (!m || !m.sets) continue;
    for (const s of safeArr(m.sets)) {
      for (const r of safeArr(s?.rallies)) {
        if (!r || !r.categories || !Array.isArray(r.categories)) continue;
        for (const cat of r.categories) {
          const playerId = cat.playerId || cat.player_id;
          const category = cat.category || cat.Category;
          if (!playerId || !category) continue;
          const entry = ensurePlayer(playerId);
          switch (category.toUpperCase()) {
            case 'A': entry.acer += 1; break;
            case 'D': entry.defender += 1; break;
            case 'L': entry.libero += 1; break;
            case 'S': entry.setter += 1; break;
            case 'B': entry.blocker += 1; break;
            case 'F': entry.finisher += 1; break;
          }
        }
      }
    }
  }

  const allPlayerStats = Array.from(playerStatsMap.values())
    .map(r => ({ ...r, total: r.acer + r.defender + r.libero + r.setter + r.blocker + r.finisher }))
    .filter(r => r.total > 0)
    .sort((a, b) => b.total - a.total);

  const playerStatsRows: (string | number)[][] = [
    ['Rank', 'Player', 'Team', 'A', 'D', 'L', 'S', 'B', 'F', 'Total'],
    ...allPlayerStats.map((row, idx) => [
      idx + 1,
      row.playerName,
      row.teamShortName,
      row.acer,
      row.defender,
      row.libero,
      row.setter,
      row.blocker,
      row.finisher,
      row.total,
    ]),
  ];

  // ── Participating Teams sheet ─────────────────────────────────────
  const participatingTeamsData: (string | number)[][] = [
    ['#', 'Team Name', 'Short Name', 'Players'],
  ];

  // Use officially registered participating teams if provided, otherwise fall back to teams with matches
  const teamsToExport = participatingTeams && participatingTeams.length > 0
    ? participatingTeams.map((t: any) => ({
        id: t.id,
        name: t.name,
        short_name: t.short_name || t.shortName,
        players: t.players || [],
      }))
    : tournamentTeams.map((t: any) => ({
        id: t.id,
        name: t.name,
        short_name: t.short_name,
        players: t.players || [],
      }));

  for (let i = 0; i < teamsToExport.length; i++) {
    const t = teamsToExport[i];
    participatingTeamsData.push([
      i + 1,
      safeStr(t.name),
      safeStr(t.short_name),
      (t.players || []).length,
    ]);
  }

  // ── Build workbook ─────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(tournamentSummaryData), 'Tournament Summary');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(participatingTeamsData), 'Participating Teams');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pointsTableRows), 'Points Table');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(matchesData), 'Matches');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(setScoresData), 'Set Scores');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pomRows), 'Player Of The Match');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(playerStatsRows), 'Player Statistics');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(tournamentDetailsData), 'Tournament Details');

  XLSX.writeFile(wb, `tournament_export.xlsx`);
  console.log('[ExportTournamentData] Excel written successfully');
}
